import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";

const RequestToMinors = () => {
  const seoData = {
    title: "Request to Minors - Livneu | Important Guidelines for Young Users",
    description:
      "Important guidelines and safety information for minors using Livneu services. Learn about parental consent requirements and safe usage practices for young viewers.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/request-to-minors",
    keywords:
      "minors guidelines, parental consent, young users safety, Livneu minors policy, child protection, safe usage",
  };

  const guidelines = [
    {
      number: "(1)",
      title: "Obtain Parental Consent Prior to Using Paid Content",
      description: "Before purchasing any paid content, merchandise, or participating in premium features, please consult with your parents or legal guardians and obtain their explicit consent in advance. This includes any transactions involving real money, premium memberships, or exclusive content access."
    },
    {
      number: "(2)", 
      title: "Confirm Your Payment Method with Parents/Guardians",
      description: "We strongly recommend consulting with your parents or legal guardians about which payment method to use, even for money that belongs to you. Never use a parent's credit card, debit card, mobile payment system, or bank account without their explicit permission and supervision."
    },
    {
      number: "(3)",
      title: "Report Accidental Purchases Immediately",
      description: "If you have purchased or used any content, merchandise, or services by mistake, please inform your parents/guardians immediately and contact the appropriate customer service as soon as possible. Quick action can often help resolve accidental transactions."
    },
    {
      number: "(4)",
      title: "Follow Age-Appropriate Content Guidelines", 
      description: "Livneu strives to maintain content suitable for diverse audiences. However, some streams or content may be intended for mature audiences. Please follow age ratings and watch content that is appropriate for your age group."
    },
    {
      number: "(5)",
      title: "Maintain Online Safety and Privacy",
      description: "Never share personal information such as your real name, address, school, or location in chat rooms or comments. Be cautious about interacting with strangers online and report any inappropriate behavior to moderators or your parents/guardians."
    }
  ];

  const paymentMethods = [
    "Credit and debit cards",
    "Mobile payment services (Dialog eZ Cash, Mobitel mCash, etc.)", 
    "Digital wallets and online banking",
    "Gift cards and vouchers",
    "Cryptocurrency (where applicable)",
    "Third-party payment platforms"
  ];

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-minors">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }}></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-kawaii-mint/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: "4s" }}></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-accent mb-6 leading-tight kawaii-glow">
                Request to Minors
              </h1>
              <motion.p
                className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Important safety guidelines and recommendations for viewers under 18 years of age
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-gray-700">
            
            {/* Introduction */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl p-6 border border-gray-700 mb-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Thank you for supporting Livneu and our content creators. We want to ensure that all our 
                  young viewers have a safe and enjoyable experience while engaging with our virtual entertainment 
                  content.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Our content includes both free entertainment and paid features such as merchandise, premium content, 
                  memberships, and interactive features. When using paid services, we ask that you follow these 
                  important guidelines to ensure your safety and the peace of mind of your parents and guardians.
                </p>
              </div>
            </motion.section>

            {/* Guidelines */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-center text-purple-400 mb-12">
                Important Guidelines for Young Users
              </h2>
              
              <div className="space-y-8">
                {guidelines.map((guideline, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="bg-gray-800/50 rounded-xl p-6 md:p-8 border border-purple-500/30"
                  >
                    <div className="flex items-start mb-4">
                      <div className="text-2xl font-bold text-purple-400 mr-4 flex-shrink-0">
                        {guideline.number}
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-black leading-tight">
                        {guideline.title}
                      </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-lg ml-12">
                      {guideline.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Payment Methods Information */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Payment Methods & Considerations</h2>
              <div className="bg-gray-800/50 rounded-xl p-6 md:p-8 border border-blue-500/30">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Various payment methods are available for Livneu services and merchandise. We highly 
                  recommend discussing the following payment options with your parents or guardians before making 
                  any purchases:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {paymentMethods.map((method, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-center"
                    >
                      <div className="w-2 h-2 bg-blue-400 rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-gray-700">{method}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                  <p className="text-red-300 font-semibold">
                    ⚠️ Important: Never use payment methods belonging to parents, family members, or others without 
                    explicit permission, even if you intend to reimburse them later.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Emergency Contact Information */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">If Something Goes Wrong</h2>
              <div className="bg-gradient-to-r from-red-500/10 to-blue-500/10 rounded-xl p-6 md:p-8 border border-red-500/30">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  If you have accidentally made a purchase, encountered inappropriate content, or experienced any 
                  issues while using our services, please take these steps immediately:
                </p>
                <ol className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <span className="text-purple-400 font-bold mr-3">1.</span>
                    <span className="text-gray-700">Tell a parent or guardian about what happened right away</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 font-bold mr-3">2.</span>
                    <span className="text-gray-700">Take a screenshot if possible to show what occurred</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 font-bold mr-3">3.</span>
                    <span className="text-gray-700">Have your parent/guardian contact our support team</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-400 font-bold mr-3">4.</span>
                    <span className="text-gray-700">Keep all receipts or confirmation emails for reference</span>
                  </li>
                </ol>
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-cyan-300 font-semibold mb-2">Contact Information:</p>
                  <p className="text-gray-700"><strong>Email:</strong> levelstair@gmail.com</p>
                  <p className="text-gray-700"><strong>Subject Line:</strong> Minor Account Issue - [Your Issue]</p>
                  <p className="text-gray-700 text-sm mt-2">
                    We prioritize issues involving minors and will respond as quickly as possible.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Digital Safety Tips */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Digital Safety Tips</h2>
              <div className="bg-gray-800/50 rounded-xl p-6 md:p-8 border border-green-500/30">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-bold text-green-400 mb-4">✅ DO:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Ask for help when you need it</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Use age-appropriate usernames</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Report inappropriate behavior</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Keep conversations public in chat</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Enjoy the entertainment responsibly</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-4">❌ DON'T:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Share personal information</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Meet strangers from online</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Use others' payment information</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Hide problems from parents</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700">Engage in or tolerate bullying</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Parental Information */}
            <motion.section 
              className="mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-purple-400 mb-6">Information for Parents and Guardians</h2>
              <div className="bg-blue-500/10 rounded-xl p-6 md:p-8 border border-blue-500/30">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Livneu is committed to providing a safe environment for viewers of all ages. We encourage 
                  parents and guardians to:
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-700">Review our content and community guidelines with your children</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-700">Set up parental controls on devices and payment methods</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-700">Monitor your child's online activities and interactions</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-700">Discuss digital spending and online safety regularly</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-700">Contact us immediately if you have any concerns</span>
                  </li>
                </ul>
                <p className="text-gray-700 text-sm">
                  For parents who need assistance with account management, payment issues, or have concerns about 
                  content, please reach out to us at <span className="text-cyan-300">levelstair@gmail.com</span>
                </p>
              </div>
            </motion.section>

            {/* Closing Message */}
            <motion.section 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-center bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 md:p-8 border border-purple-500/30">
                <h2 className="text-2xl font-bold text-black mb-4">Thank You for Your Support!</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  By following these guidelines and maintaining open communication with your parents or guardians, 
                  you can safely enjoy all the exciting content that Livneu has to offer.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Thank you for being part of our community and supporting Livneu's mission to bring 
                  joy and entertainment to viewers across Sri Lanka and South Asia.
                </p>
                <div className="mt-6 text-sm text-gray-400">
                  Last updated: January 2025
                </div>
              </div>
            </motion.section>

            </div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default RequestToMinors;
